import React, { useState } from 'react';
import { X, Camera, MapPin, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

const StopManagementModal = ({ isOpen, onClose, stop, routeId, onSuccess }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [evidence, setEvidence] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [notes, setNotes] = useState('');
    const [coords, setCoords] = useState(null);

    // Función unificada de captura GPS
    const getCurrentGPS = () => {
        return new Promise((resolve, reject) => {
            if (!("geolocation" in navigator)) {
                reject(new Error("Tu dispositivo no soporta geolocalización."));
                return;
            }
            navigator.geolocation.getCurrentPosition(
                (pos) => resolve(pos.coords),
                (err) => {
                    console.error('GPS Error:', err);
                    let msg = "No se pudo obtener la ubicación.";
                    if (err.code === 1) msg = "Permiso de GPS denegado. Actívalo para continuar.";
                    else if (err.code === 2) msg = "Ubicación no disponible. Verifica tu señal GPS.";
                    else if (err.code === 3) msg = "Tiempo de espera agotado al obtener GPS.";
                    reject(new Error(msg));
                },
                { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
            );
        });
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            // Capturar GPS ANTES de permitir la foto (Obligatorio para KPI)
            toast.loading('Capturando ubicación GPS...', { id: 'gps-load' });
            const currentCoords = await getCurrentGPS();
            setCoords({ latitude: currentCoords.latitude, longitude: currentCoords.longitude });
            toast.success('Ubicación capturada', { id: 'gps-load' });

            setEvidence(file);
            setPreviewUrl(URL.createObjectURL(file));
        } catch (err) {
            toast.error(err.message, { id: 'gps-load' });
            e.target.value = ''; // Limpiar input
        }
    };

    const handleComplete = async () => {
        if (!evidence) {
            toast.error('La foto de evidencia es obligatoria');
            return;
        }

        if (!coords) {
            toast.error('Ubicación GPS obligatoria');
            return;
        }

        try {
            setIsSubmitting(true);

            const formData = new FormData();
            formData.append('evidence', evidence);
            formData.append('notes', notes);
            formData.append('latitude', coords.latitude);
            formData.append('longitude', coords.longitude);

            const res = await api.put(`/routes/${routeId}/additional-stops/${stop.id}/complete`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (res.data.success) {
                toast.success('✅ Parada completada exitosamente');
                onSuccess();
                handleClose();
            }
        } catch (error) {
            console.error('Error completing stop:', error);
            toast.error(error.response?.data?.message || 'Error al completar parada');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setEvidence(null);
        setPreviewUrl(null);
        setNotes('');
        setCoords(null);
        onClose();
    };

    if (!isOpen || !stop) return null;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden flex flex-col animate-in fade-in zoom-in duration-200">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-purple-600">
                    <div>
                        <h2 className="text-lg font-bold text-white uppercase tracking-tight">Gestionar Parada Extra</h2>
                        <p className="text-xs text-purple-100 opacity-80">#{stop.sequenceNumber} - {stop.stop_type || 'OTRA'}</p>
                    </div>
                    <button onClick={handleClose} className="text-white/80 hover:text-white transition-colors p-1 rounded-full hover:bg-white/10">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 space-y-6 overflow-y-auto max-h-[70vh]">
                    {/* Info Card */}
                    <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                        <div className="flex items-start gap-3">
                            <MapPin className="w-5 h-5 text-purple-600 mt-0.5" />
                            <div>
                                <p className="text-sm font-bold text-gray-900 leading-tight">{stop.address}</p>
                                {stop.description && <p className="text-xs text-gray-600 mt-1 italic">{stop.description}</p>}
                            </div>
                        </div>
                    </div>

                    {/* Photo Evidence */}
                    <div className="space-y-3">
                        <label className="block text-sm font-black text-gray-700 uppercase tracking-wider">Evidencia Fotográfica *</label>
                        {!previewUrl ? (
                            <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-purple-300 rounded-2xl bg-purple-50/50 hover:bg-purple-50 transition-colors cursor-pointer group">
                                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                    <div className="p-4 bg-purple-100 rounded-full group-hover:scale-110 transition-transform mb-3">
                                        <Camera className="w-8 h-8 text-purple-600" />
                                    </div>
                                    <p className="text-sm font-bold text-purple-700">Tomar Foto</p>
                                    <p className="text-[10px] text-purple-400 mt-1 uppercase">Obligatorio con GPS</p>
                                </div>
                                <input
                                    type="file"
                                    accept="image/*"
                                    capture="environment"
                                    className="hidden"
                                    onChange={handleFileChange}
                                />
                            </label>
                        ) : (
                            <div className="relative rounded-2xl overflow-hidden border-2 border-purple-200">
                                <img src={previewUrl} alt="Preview" className="w-full h-48 object-cover" />
                                <button
                                    onClick={() => { setPreviewUrl(null); setEvidence(null); setCoords(null); }}
                                    className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full shadow-lg hover:bg-red-700"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                                {coords && (
                                    <div className="absolute bottom-2 left-2 bg-black/50 backdrop-blur-md px-2 py-1 rounded text-[9px] text-white flex items-center gap-1">
                                        <CheckCircle2 className="w-3 h-3 text-green-400" /> GPS Capturado
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Notes */}
                    <div className="space-y-2">
                        <label className="block text-sm font-black text-gray-700 uppercase tracking-wider">Notas / Observaciones</label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Ej: Se entregó paquete en portería..."
                            rows={3}
                            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all outline-none text-sm placeholder:text-gray-400"
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className="p-6 bg-gray-50 border-t border-gray-100">
                    <button
                        onClick={handleComplete}
                        disabled={isSubmitting || !evidence || !coords}
                        className="w-full bg-purple-600 text-white py-4 rounded-xl font-black text-sm uppercase tracking-widest shadow-xl shadow-purple-200 hover:bg-purple-700 active:scale-[0.98] transition-all disabled:opacity-50 disabled:pointer-events-none flex items-center justify-center gap-2"
                    >
                        {isSubmitting ? (
                            <>
                                <Loader2 className="w-5 h-5 animate-spin" />
                                Procesando...
                            </>
                        ) : (
                            <>
                                <CheckCircle2 className="w-5 h-5" />
                                Completar Parada
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default StopManagementModal;
